import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BookMarked, KeyRound, LogOut, ReceiptText, Settings, Trash2, UserRound, X } from 'lucide-react';
import { antiBrushHeaders } from '../utils/analytics';

const apiBaseUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:65181'}${process.env.REACT_APP_API_VERSION === undefined ? '/api/v1' : process.env.REACT_APP_API_VERSION}`;

export interface AccountUser {
  id: number;
  username: string;
  nickname?: string | null;
  avatar_url?: string | null;
}

interface UserAccountMenuProps {
  user: AccountUser;
  setUser: (user: AccountUser | null) => void;
  onAccount?: () => void;
  onOrders?: () => void;
  onBookshelf?: () => void;
  onProfile?: () => void;
  onLogoutComplete?: () => void;
  className?: string;
}

type AccountDialog = 'password' | 'delete' | null;

const avatarInitial = (value?: string | null) => (value || '灵').trim().slice(0, 1).toUpperCase();

const clearLoginStorage = () => {
  localStorage.removeItem('lingbi-token');
  localStorage.removeItem('lingbi-user');
};

const authHeaders = () => {
  const token = localStorage.getItem('lingbi-token');
  return {
    'Content-Type': 'application/json',
    ...antiBrushHeaders(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const parseError = async (response: Response, fallback: string) => {
  const data = await response.json().catch(() => ({}));
  return data.detail || data.message || fallback;
};

const UserAccountMenu: React.FC<UserAccountMenuProps> = ({
  user,
  setUser,
  onAccount,
  onOrders,
  onBookshelf,
  onProfile,
  onLogoutComplete,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuPosition, setMenuPosition] = useState<React.CSSProperties>({
    top: 0,
    left: 0,
    minWidth: 172,
  });
  const [dialog, setDialog] = useState<AccountDialog>(null);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const displayName = user.nickname || user.username;

  const syncMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const width = Math.max(172, Math.ceil(rect.width));
    const margin = 12;
    const gap = 10;
    const left = Math.max(margin, Math.min(window.innerWidth - width - margin, rect.right - width));
    setMenuPosition({
      top: Math.round(rect.bottom + gap),
      left: Math.round(left),
      minWidth: width,
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    syncMenuPosition();

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', syncMenuPosition);
    window.addEventListener('scroll', syncMenuPosition, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', syncMenuPosition);
      window.removeEventListener('scroll', syncMenuPosition, true);
    };
  }, [open, syncMenuPosition]);

  const resetDialog = () => {
    setMessage('');
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setDeletePassword('');
  };

  const closeLogin = () => {
    clearLoginStorage();
    setUser(null);
    setOpen(false);
    onLogoutComplete?.();
  };

  const logout = async () => {
    try {
      await fetch(`${apiBaseUrl}/users_app/me/logout`, {
        method: 'POST',
        headers: authHeaders(),
      });
    } catch {
      // 本地退出必须始终可用。
    }
    closeLogin();
  };

  const submitPassword = async () => {
    if (!oldPassword || !newPassword || !confirmPassword || busy) return;
    setBusy(true);
    setMessage('正在修改密码...');
    try {
      const response = await fetch(`${apiBaseUrl}/users_app/me/password`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          old_password: oldPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      });
      if (!response.ok) throw new Error(await parseError(response, '修改密码失败'));
      setMessage('密码已修改，请重新登录。');
      window.setTimeout(() => {
        setDialog(null);
        closeLogin();
        window.location.hash = '#login';
      }, 700);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '修改密码失败');
    } finally {
      setBusy(false);
    }
  };

  const submitDelete = async () => {
    if (!deletePassword || busy) return;
    setBusy(true);
    setMessage('正在注销账户...');
    try {
      const response = await fetch(`${apiBaseUrl}/users_app/me`, {
        method: 'DELETE',
        headers: authHeaders(),
        body: JSON.stringify({ password: deletePassword }),
      });
      if (!response.ok) throw new Error(await parseError(response, '注销账户失败'));
      setMessage('账户已注销。');
      window.setTimeout(() => {
        setDialog(null);
        closeLogin();
        window.location.hash = '#home';
      }, 700);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '注销账户失败');
    } finally {
      setBusy(false);
    }
  };

  const openDialog = (next: AccountDialog) => {
    resetDialog();
    setOpen(false);
    setDialog(next);
  };

  const menu = open && typeof document !== 'undefined' ? createPortal(
    <div ref={menuRef} className="site-user-menu lumi-user-menu site-user-menu-portal" style={menuPosition}>
      {onAccount && <button type="button" onClick={() => { setOpen(false); onAccount(); }}><UserRound size={15} />账户中心</button>}
      {onOrders && <button type="button" onClick={() => { setOpen(false); onOrders(); }}><ReceiptText size={15} />我的订单</button>}
      {onBookshelf && <button type="button" onClick={() => { setOpen(false); onBookshelf(); }}><BookMarked size={15} />我的书架</button>}
      {onProfile && <button type="button" onClick={() => { setOpen(false); onProfile(); }}><Settings size={15} />个人设置</button>}
      <button type="button" onClick={() => openDialog('password')}><KeyRound size={15} />修改密码</button>
      <button type="button" onClick={logout}><LogOut size={15} />退出登录</button>
      <button className="danger" type="button" onClick={() => openDialog('delete')}><Trash2 size={15} />注销账户</button>
    </div>,
    document.body,
  ) : null;

  return (
    <div className={`site-user lumi-user ${className}`}>
      <button
        ref={triggerRef}
        className="site-user-trigger lumi-user-trigger"
        type="button"
        onClick={() => {
          syncMenuPosition();
          setOpen((value) => !value);
        }}
      >
        {user.avatar_url ? (
          <img src={user.avatar_url} alt={displayName} />
        ) : (
          <span className="site-user-fallback">{avatarInitial(displayName)}</span>
        )}
        <strong>{displayName}</strong>
      </button>
      {menu}
      {dialog && (
        <div className="account-dialog-backdrop" role="dialog" aria-modal="true" aria-label={dialog === 'password' ? '修改密码' : '注销账户'}>
          <section className="account-dialog">
            <button className="account-dialog-close" type="button" aria-label="关闭" onClick={() => setDialog(null)}>
              <X size={16} />
            </button>
            {dialog === 'password' ? (
              <>
                <h3>修改密码</h3>
                <p>修改成功后需要重新登录。</p>
                <label><span>旧密码</span><input type="password" value={oldPassword} onChange={(event) => setOldPassword(event.target.value)} /></label>
                <label><span>新密码</span><input type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} /></label>
                <label><span>确认新密码</span><input type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} /></label>
                <button className="account-dialog-primary" type="button" disabled={busy || !oldPassword || !newPassword || !confirmPassword} onClick={submitPassword}>
                  {busy ? '提交中' : '确认修改'}
                </button>
              </>
            ) : (
              <>
                <h3>注销账户</h3>
                <p>注销后该账号将不能继续登录，请输入当前密码确认。</p>
                <label><span>当前密码</span><input type="password" value={deletePassword} onChange={(event) => setDeletePassword(event.target.value)} /></label>
                <button className="account-dialog-primary danger" type="button" disabled={busy || !deletePassword} onClick={submitDelete}>
                  {busy ? '注销中' : '确认注销'}
                </button>
              </>
            )}
            {message && <div className="account-dialog-message">{message}</div>}
          </section>
        </div>
      )}
    </div>
  );
};

export default UserAccountMenu;
